import { useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet, Download, BedDouble, Landmark } from "lucide-react";
import { toast } from "sonner";

const download = async (fmt, filename, setBusy) => {
  setBusy(fmt);
  try {
    const res = await api.get(`/guests/export/${fmt}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Export scaricato");
  } catch (err) {
    toast.error("Export non riuscito");
  } finally {
    setBusy(null);
  }
};

export default function GuestExports() {
  const [busy, setBusy] = useState(null);

  const cards = [
    {
      fmt: "alloggiati",
      file: "schedine_alloggiati.csv",
      icon: BedDouble,
      title: "Schedine Alloggiati",
      desc: "Elenco ospiti con data di arrivo, giorni di permanenza, cognome, nome, cittadinanza con codice Stato ufficiale (Tabella Stati Alloggiati Web) e codice fiscale — pronto per il caricamento su Alloggiati Web.",
      tint: "#C86D51",
    },
    {
      fmt: "istat",
      file: "movimento_clienti_istat.csv",
      icon: Landmark,
      title: "Movimento Clienti ISTAT",
      desc: "Riepilogo di arrivi, clienti e presenze aggregati per Paese di residenza, per la rilevazione statistica del movimento turistico.",
      tint: "#4A6B5D",
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">Adempimenti</p>
        <h1 className="font-display text-4xl sm:text-5xl mt-1">Schedine & ISTAT</h1>
        <p className="text-muted-foreground mt-1">Esporta i dati degli ospiti per gli obblighi di legge.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-5" data-testid="exports-grid">
        {cards.map((c) => (
          <Card key={c.fmt} className="p-6 border-border shadow-sm animate-fade-up">
            <div className="w-12 h-12 rounded-xl grid place-items-center mb-4" style={{ background: c.tint }}>
              <c.icon className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-heading text-xl font-semibold">{c.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mt-2 mb-5">{c.desc}</p>
            <Button
              data-testid={`export-${c.fmt}`}
              onClick={() => download(c.fmt, c.file, setBusy)}
              disabled={busy === c.fmt}
              className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full"
            >
              <Download className="w-4 h-4 mr-2" />
              {busy === c.fmt ? "Preparazione…" : "Scarica CSV"}
            </Button>
          </Card>
        ))}
      </div>

      <Card className="p-5 border-border bg-secondary/30 flex items-start gap-3">
        <FileSpreadsheet className="w-5 h-5 text-accent mt-0.5 shrink-0" />
        <p className="text-sm text-muted-foreground leading-relaxed">
          I file sono in formato CSV (separatore <code>;</code>, codifica UTF-8) e si aprono in Excel, Numbers o LibreOffice.
          Le prenotazioni annullate sono escluse automaticamente.
        </p>
      </Card>
    </div>
  );
}
